import React, {
  useRef,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import { DragDropContext } from 'react-beautiful-dnd';
import IconSideBar from './IconSideBar';
import AnimatedSideBarPrimary from './AnimatedSideBarPrimary';
import CalendarPanel from './CalendarPanel';
import SubHeader from './SubHeader';
import Text from '../../../../shared/components/Text/Text';
import useDragManager from '../../hooks/useDragManager';
import ProgramLibrarySubHeader from './ProgramLibrarySubHeader';
import ProgramLibraryFooter from './ProgramLibraryFooter';
import WeekViewDayToggleFooter from './WeekViewDayToggleFooter';
import { hasKeys } from '../../../../shared/utils/isEmptyObject';

import {
  setCalendarContainerScrollPositionTop,
  setDroppableContainerCurrentlyHoveredByDraggedItem,
} from '../../ducks/calendarActions';

const CalendarContentAndStats = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  ${(props) => props.viewType !== 'weekView' && 'overflow-x: hidden;'}
  ${(props) => props.viewType === 'weekView' && 'padding-left: 48px;'}
`;

const StatsBar = styled('div')`
  background: white;
  min-height: 40px;
  border-top: 1px solid #EEEEEE;
  width: 100%;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const MonthYearWeekSubHeaderWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  margin: 8px;
  height: 100%;
  ${(props) => props.viewType !== 'weekView' && 'overflow-x: hidden;'}
  ${(props) => props.viewType === 'weekView' && !props.noCalendarSelected && props.programType !== 'free-form-program' && 'padding-top: 32px;'}
  ${(props) => props.viewType === 'weekView' && !props.noCalendarSelected && 'padding-bottom: 48px;'}
`;

const SubHeaderTextWrapper = styled('div')`
  display: flex;
  width: 100%;
  min-height: 32px;
  max-height: 32px;
  background: white;
  align-items: center;
  padding-left: 10px;
  gap: 4px;
  ${(props) => props.viewType === 'weekView' && 'position: fixed;'}
`;

const WeekWrapper = styled('div')`
  display: flex;
  padding: 4px;
  border-radius: 4px;
  background: #F5F5F5;
  margin-left: 4px;
`;

const StatsBarContent = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 16px;
`;

const StatsBarItem = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const CalendarContentContainer = styled('div')`
  display: flex;
  width: 100%;
  background-color: white;
  ${(props) => props.viewType !== 'weekView' && 'overflow-x: hidden;'}
  flex: 1;
  padding-left: ${(props) => props.viewType === 'dayView' && '5px'};
  padding-right: ${(props) => props.viewType === 'dayView' && '5px'};

  /* Hide the default scrollbar track */
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  /* Define scrollbar width */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  /* Style the scrollbar thumb */
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
  
  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

`;

const CalendarContent = () => {
  const containerRef = useRef(null);
  const lastHoveredDroppableRef = useRef(null);
  const dispatch = useDispatch();
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const date = new Date(selectedDate);
  const endpointFormattedDate = moment(date).format('YYYY-MM-DD');
  const setCount = programType === 'free-form-program' ? useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItems[
      selectedProgramDayNumber]?.setCount || 0,
  ) : useSelector(
    (state) => state.calendar.data.calendarItems[endpointFormattedDate]?.setCount || 0,
  );
  const repCount = programType === 'free-form-program' ? useSelector((state) => state.calendar.data.nonDateBasedProgramItems[
    selectedProgramDayNumber
  ]?.repCount || 0) : useSelector(
    (state) => state.calendar.data.calendarItems[endpointFormattedDate]?.repCount || 0,
  );
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const isAnalyticsDisabled = useSelector(
    (state) => state.calendar.ui.isAnalyticsDisabled,
  );
  const isPrimaryAnimatedSideBarOpen = useSelector(
    (state) => state.calendar.data.isPrimaryAnimatedSideBarOpen,
  );
  const currentlyDisplayedWeekdays = useSelector(
    (state) => state.calendar.data.currentlyDisplayedWeekdays,
  );
  const selectedWeekViewDayToggle = useSelector(
    (state) => state.calendar.data.selectedWeekViewDayToggle,
  );

  const noCalendarSelected = (!hasKeys(selectedReduxCalendar)
  && !hasKeys(selectedReduxAthlete)
  && !hasKeys(selectedReduxNonDateBasedProgram));

  // Use the drag manager hook
  const { handleDragEnd, handleDragStart, handleOnBeforeCapture } = useDragManager();

  useEffect(() => {
    /** set info about top of scroll container to redux
       * we need this for tool tips
       */
    if (containerRef?.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      dispatch(setCalendarContainerScrollPositionTop(containerRect.top));
    }
  }, [dispatch]);

  const getWeekForLastDayInDayToggleView = () => {
    const lastItem = currentlyDisplayedWeekdays[currentlyDisplayedWeekdays.length - 1];
    const lastItemWeek = moment(lastItem?.date).week();
    return lastItemWeek;
  };

  return (
    <DragDropContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onBeforeCapture={handleOnBeforeCapture}
      onDragUpdate={(update) => {
        const destinationDroppableId = update?.destination?.droppableId;

        if (destinationDroppableId && lastHoveredDroppableRef.current !== destinationDroppableId) {
          lastHoveredDroppableRef.current = destinationDroppableId;
          dispatch(setDroppableContainerCurrentlyHoveredByDraggedItem(destinationDroppableId));
        }
      }}
    >
      <IconSideBar />
      <AnimatedSideBarPrimary />
      <CalendarContentAndStats
        viewType={viewType}
        isPrimaryAnimatedSideBarOpen={isPrimaryAnimatedSideBarOpen}
      >
        {((hasKeys(selectedReduxCalendar) && programType !== 'free-form-program') || (hasKeys(selectedReduxAthlete) && programType !== 'free-form-program')) ? (
          <SubHeaderTextWrapper viewType={viewType}>
            <Text fontSize='12px' color='#444444'>{moment(selectedDate).format('MMM')}</Text>
            <Text fontSize='12px' color='#9E9E9E'>{moment(selectedDate).format('YYYY')}</Text>
            <WeekWrapper>
              <Text fontSize='12px' color='#9E9E9E'>
                W
                {moment(selectedDate).week()}
                {viewType === 'weekView' && selectedWeekViewDayToggle !== 'Everyday' && ` - W${getWeekForLastDayInDayToggleView()}`}
              </Text>
            </WeekWrapper>
          </SubHeaderTextWrapper>
        ) : null}
        <MonthYearWeekSubHeaderWrapper
          viewType={viewType}
          noCalendarSelected={noCalendarSelected}
          programType={programType}
        >
          {(hasKeys(selectedReduxNonDateBasedProgram) && programType === 'free-form-program' && viewType !== 'weekView') ? (
            <ProgramLibrarySubHeader />
          ) : null}
          {(((hasKeys(selectedReduxCalendar) && programType !== 'free-form-program') || (hasKeys(selectedReduxAthlete))) && viewType === 'dayView' && programType !== 'free-form-program') ? (
            <SubHeader />
          ) : null}
          <CalendarContentContainer
            ref={containerRef}
            viewType={viewType}
          >
            <CalendarPanel scrollContainerRef={containerRef} />
          </CalendarContentContainer>
          {((hasKeys(selectedReduxCalendar)
           || hasKeys(selectedReduxAthlete)
            || hasKeys(selectedReduxNonDateBasedProgram))) && !isAnalyticsDisabled && viewType !== 'weekView' ? (
              <StatsBar>
                <StatsBarContent>
                  <StatsBarItem>
                    <Text fontSize='13px' color='#BDBDBD'>Sets</Text>
                    <Text
                      marginLeft='5px'
                      marginTop='2px'
                      fontSize='15px'
                      color='#616161'
                      fontWeight='500'
                    >
                      {setCount}
                    </Text>
                  </StatsBarItem>
                  <StatsBarItem>
                    <Text fontSize='13px' color='#BDBDBD'>Reps</Text>
                    <Text
                      marginLeft='5px'
                      marginTop='2px'
                      fontSize='15px'
                      color='#616161'
                      fontWeight='500'
                    >
                      {repCount}
                    </Text>
                  </StatsBarItem>
                </StatsBarContent>
              </StatsBar>
            ) : null}
        </MonthYearWeekSubHeaderWrapper>
        {hasKeys(selectedReduxNonDateBasedProgram) && programType === 'free-form-program' ? (
          <ProgramLibraryFooter />
        ) : null}
        {programType === 'calendar' && viewType === 'weekView' && (hasKeys(selectedReduxCalendar)
           || hasKeys(selectedReduxAthlete)) ? (
             <WeekViewDayToggleFooter />
          ) : null }
      </CalendarContentAndStats>
    </DragDropContext>
  );
};

export default CalendarContent;
